import { useQuery } from "@tanstack/react-query";
import { from, rpc, unwrap } from "@vixio/data";
import type { StatusMap } from "@vixio/ui";

export const BOOKING_STATUS: StatusMap = {
  confirmed: { label: "confirmada", tone: "info" },
  completed: { label: "realizada", tone: "ok" },
  no_show: { label: "falta", tone: "warn" },
  canceled: { label: "cancelada", tone: "neutral" },
  rescheduled: { label: "remarcada", tone: "neutral" },
};
export const ORIGIN: Record<string, string> = { site: "site", gestao: "gestão", atendimento: "atendimento", workflow: "automação", portal: "portal do cliente" };
export const MEETING_STATUS: StatusMap = {
  agendada: { label: "agendada", tone: "info" },
  realizada: { label: "realizada", tone: "ok" },
  cancelada: { label: "cancelada", tone: "neutral" },
};
export const TASK_STATUS: StatusMap = {
  aberta: { label: "aberta", tone: "warn" },
  concluida: { label: "concluída", tone: "ok" },
  cancelada: { label: "cancelada", tone: "neutral" },
};
export const LOCATION: Record<string, string> = { online: "Online", presencial: "Presencial", telefone: "Telefone" };
export const WEEKDAYS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"];
export const GENERATED_BY: Record<string, string> = { humano: "escrita pela equipe", ia: "gerada por IA", ia_revisada: "gerada por IA e revisada" };

export interface BookingView {
  id: string;
  calendar_id: string;
  calendar_name: string;
  type_key: string;
  type_title: string;
  person_id: string | null;
  person_name: string | null;
  person_email: string | null;
  person_phone: string | null;
  starts_at: string;
  ends_at: string;
  timezone: string;
  local_date: string;
  local_time: string;
  status: string;
  origin: string;
  opportunity_id: string | null;
  notes: string | null;
  rescheduled_from: string | null;
  cancel_reason: string | null;
  created_at: string;
}

export interface Calendar {
  id: string;
  slug: string;
  name: string;
  owner_user: string | null;
  timezone: string;
  google_calendar_id: string | null;
  active: boolean;
}

export interface AvailabilityRule {
  id: string;
  calendar_id: string;
  weekday: number;
  start_minute: number;
  end_minute: number;
}

export interface Block {
  id: string;
  calendar_id: string;
  starts_at: string;
  ends_at: string;
  reason: string | null;
  source: string;
}

export interface BookingType {
  id: string;
  calendar_id: string;
  key: string;
  title: string;
  description: string | null;
  duration_min: number;
  buffer_before_min: number;
  buffer_after_min: number;
  slot_interval_min: number | null;
  min_notice_min: number;
  window_days: number;
  min_cancel_notice_min: number;
  location_kind: string;
  location_detail: string | null;
  reminder_minutes: number[];
  reminder_channel: string;
  consent_purpose: string;
  require_consent: boolean;
  max_active_per_person: number;
  is_public: boolean;
  /** Liberado para o cliente reservar pelo portal (migration 0005). */
  client_bookable: boolean;
  active: boolean;
}

export interface Meeting {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string;
  location: string | null;
  booking_id: string | null;
  opportunity_id: string | null;
  status: "agendada" | "realizada" | "cancelada";
  created_at: string;
}

export interface Participant {
  id: string;
  meeting_id: string;
  person_id: string | null;
  name: string;
  email: string | null;
  role: string;
}

export interface AgendaItem {
  id: string;
  position: number;
  topic: string;
  discussed: boolean;
  notes: string | null;
}

export interface Minutes {
  id: string;
  meeting_id: string;
  version: number;
  content: string;
  agenda: Array<{ topic: string; discussed?: boolean; notes?: string | null }>;
  decisions: Array<{ text: string }>;
  action_items: Array<{ text: string; responsible?: string | null; due_date?: string | null }>;
  generated_by: string;
  content_sha256: string;
  created_at: string;
}

export interface Task {
  id: string;
  meeting_id: string;
  minutes_id: string;
  text: string;
  responsible_person_id: string | null;
  responsible_name: string | null;
  due_on: string | null;
  status: "aberta" | "concluida" | "cancelada";
  done_at: string | null;
  created_at: string;
}

/** Reunião ou reserva de que o usuário participa (agenda.my_meetings). */
export interface MyMeeting {
  kind: "meeting" | "booking";
  id: string;
  title: string;
  starts_at: string;
  ends_at: string;
  location: string | null;
  status: "scheduled" | "done" | "canceled";
  room_id: string | null;
  opens_at: string;
  closes_at: string;
  can_cancel: boolean;
}

export interface VideoJoin {
  room: string;
  jwt: string;
  expires_at: string;
  moderator: boolean;
  display_name: string;
  title: string;
  starts_at: string;
  ends_at: string;
}

export interface VideoRoom {
  id: string;
  meeting_id: string | null;
  booking_id: string | null;
  closed_at: string | null;
}

export interface ClientType {
  key: string;
  title: string;
  description: string | null;
  duration_min: number;
  location_kind: string;
  timezone: string;
  min_cancel_notice_min: number;
}

export interface Slot {
  starts_at: string;
  ends_at: string;
  local_date: string;
  local_time: string;
}

type U = string;
declare module "@vixio/data" {
  interface RpcRegistry {
    "agenda.install_defaults": { args: { p_org: U }; returns: U };
    "agenda.set_google_sync": {
      args: {
        p_calendar: U; p_connection: U; p_google_calendar_id?: string; p_import_busy?: boolean; p_push_bookings?: boolean;
        p_lookahead_days?: number; p_active?: boolean; p_extra_busy_calendars?: string[];
      };
      returns: U;
    };
    "agenda.retry_google_pushes": { args: { p_calendar: U }; returns: number };
    "agenda.upsert_calendar": {
      args: { p_org: U; p_slug: string; p_name: string; p_timezone?: string; p_owner_user?: U | null; p_google_calendar_id?: string | null; p_active?: boolean };
      returns: U;
    };
    "agenda.set_weekly_availability": { args: { p_calendar: U; p_windows: Array<{ weekday: number; start: string; end: string }> }; returns: number };
    "agenda.add_block": { args: { p_calendar: U; p_starts_at: string; p_ends_at: string; p_reason?: string | null }; returns: U };
    "agenda.remove_block": { args: { p_block: U }; returns: null };
    "agenda.upsert_booking_type": { args: { p_calendar: U; p_key: string; p_title: string; p_duration_min: number; p_options?: Record<string, unknown> }; returns: U };
    "agenda.team_slots": {
      args: { p_org: U; p_type_key: string; p_from?: string | null; p_to?: string | null };
      returns: { type: { key: string; title: string; duration_min: number; is_public: boolean }; timezone: string; slots: Slot[] };
    };
    "agenda.create_booking": {
      args: { p_org: U; p_type_key: string; p_starts_at: string; p_person: U; p_opportunity_id?: U | null; p_notes?: string | null; p_respect_rules?: boolean; p_origin?: string };
      returns: U;
    };
    "agenda.reschedule_booking": { args: { p_booking: U; p_new_starts_at: string; p_respect_rules?: boolean }; returns: U };
    "agenda.cancel_booking": { args: { p_booking: U; p_reason: string }; returns: null };
    "agenda.mark_attendance": { args: { p_booking: U; p_attended: boolean }; returns: null };
    "agenda.create_meeting": {
      args: { p_org: U; p_title: string; p_starts_at: string; p_ends_at: string; p_location?: string | null; p_booking_id?: U | null; p_opportunity_id?: U | null };
      returns: U;
    };
    "agenda.set_meeting_status": { args: { p_meeting: U; p_status: string }; returns: null };
    "agenda.add_participant": { args: { p_meeting: U; p_name: string; p_email?: string | null; p_person_id?: U | null; p_role?: string; p_user_id?: U | null }; returns: U };
    "agenda.set_agenda": { args: { p_meeting: U; p_items: Array<{ topic: string }> }; returns: number };
    "agenda.mark_item": { args: { p_item: U; p_discussed: boolean; p_notes?: string | null }; returns: null };
    "agenda.publish_minutes": {
      args: { p_meeting: U; p_content: string; p_decisions?: Array<{ text: string }>; p_action_items?: Array<{ text: string; responsible?: string | null; responsible_person_id?: U | null; due_date?: string | null }>; p_generated_by?: string };
      returns: U;
    };
    "agenda.set_task_status": { args: { p_task: U; p_status: string }; returns: null };
    "agenda.enable_video": { args: { p_meeting?: U | null; p_booking?: U | null }; returns: U };
    "agenda.close_video": { args: { p_room: U }; returns: null };
    "agenda.join_video": { args: { p_room: U }; returns: VideoJoin };
    "agenda.my_meetings": { args: { p_org: U; p_include_past?: boolean }; returns: MyMeeting[] };
    "agenda.set_client_bookable": { args: { p_type: U; p_enabled: boolean }; returns: null };
    "agenda.client_types": { args: { p_org: U }; returns: ClientType[] };
    "agenda.client_slots": {
      args: { p_org: U; p_type_key: string; p_from?: string | null; p_to?: string | null };
      returns: { type: { key: string; title: string; duration_min: number }; timezone: string; slots: Slot[] };
    };
    "agenda.client_book": {
      args: { p_org: U; p_type_key: string; p_starts_at: string; p_notes?: string | null };
      returns: { booking_id: U; starts_at: string; ends_at: string; local_date: string; local_time: string; room_id?: U };
    };
    "agenda.client_cancel": { args: { p_booking: U; p_reason?: string | null }; returns: null };
  }
}

export function useTypes(orgId: string) {
  return useQuery({
    queryKey: ["agenda", "types", orgId],
    queryFn: () => unwrap<BookingType[]>(from("agenda", "booking_types").select("*").eq("org_id", orgId).order("title")),
  });
}

export function useCalendars(orgId: string) {
  return useQuery({
    queryKey: ["agenda", "calendars", orgId],
    queryFn: () => unwrap<Calendar[]>(from("agenda", "calendars").select("*").eq("org_id", orgId).order("name")),
  });
}

export function minutesToHhmm(m: number): string {
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
}

/** "2026-09-28" → "seg, 28/09". */
export function shortDay(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number) as [number, number, number];
  const wd = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return `${["dom", "seg", "ter", "qua", "qui", "sex", "sáb"][wd]}, ${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}`;
}

/** Fuso em texto curto: "horário de Brasília" em vez de "America/Sao_Paulo". */
export function tzLabel(tz: string): string {
  const known: Record<string, string> = { "America/Sao_Paulo": "horário de Brasília", "America/Bahia": "horário da Bahia" };
  return known[tz] ?? (tz.split("/").pop() ?? tz).replace(/_/g, " ");
}

export function useMyMeetings(orgId: string, includePast = false) {
  return useQuery({
    queryKey: ["agenda", "mine", orgId, includePast],
    queryFn: () => rpc("agenda", "my_meetings", { p_org: orgId, p_include_past: includePast }),
    refetchInterval: 60_000,
  });
}

/** A sala aceita entrada agora? (15 min antes do início até 30 min depois do fim, pelo banco.) */
export function roomOpen(m: Pick<MyMeeting, "room_id" | "opens_at" | "closes_at" | "status">, now = Date.now()): boolean {
  return !!m.room_id && m.status !== "canceled" && Date.parse(m.opens_at) <= now && now <= Date.parse(m.closes_at);
}
